/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type MutationFunctionContext, useMutation } from '@tanstack/react-query';
import type { SubmitReviewResponse } from '../interface/bv-types';
import { BV_BASE_URL, BV_PASS_KEY } from '../constants';
import { useLocale } from '@/hooks/useLocale';
import { localStorage } from '@/utils/localStorage';
import axios, { type AxiosResponse } from 'axios';

const URL = `${BV_BASE_URL}/data/submitreview.json`;

const selector = (
  response: AxiosResponse<SubmitReviewResponse>,
): {
  data?: SubmitReviewResponse;
  hasSubmitted: boolean;
} => {
  return {
    data: response.data,
    hasSubmitted:
      response.data.Errors?.some((error) => error.Code === 'ERROR_DUPLICATE_SUBMISSION') ?? false,
  };
};

type FetchReviewFormDataReturnValue = {
  data?: SubmitReviewResponse;
  hasSubmitted: boolean;
};

const fetchReviewFormData = async (
  productId: string | undefined,
  locale: string,
): Promise<FetchReviewFormDataReturnValue> => {
  try {
    const response = await axios.get<SubmitReviewResponse>(URL, {
      params: {
        locale,
        productid: productId,
        user: localStorage.getItem('reviewSubmissionId'),
        ApiVersion: 5.4,
        PassKey: BV_PASS_KEY,
      },
    });
    return selector(response);
  } catch {
    return { hasSubmitted: false }; // Return false when an error occurs to still allow user submit a review
  }
};

type Options = {
  productId: string | undefined;
  onSuccess?: (
    data: FetchReviewFormDataReturnValue,
    variables: void,
    onMutateResult: unknown,
    context: MutationFunctionContext,
  ) => void;
  onError?: (
    error: Error,
    variables: void,
    onMutateResult: unknown,
    context: MutationFunctionContext,
  ) => void;
};

type UseProductReviewDataReturnValue = {
  data: SubmitReviewResponse | undefined;
  hasSubmitted: boolean | undefined;
  status: string;
  isPending: boolean;
  isInitialLoading: boolean;
  mutate: () => void;
  mutateAsync: () => Promise<FetchReviewFormDataReturnValue>;
};

export const useReviewFormData = ({
  productId,
  onSuccess,
  onError,
}: Options): UseProductReviewDataReturnValue => {
  const locale = useLocale().replace('-', '_');

  const { data, status, isPending, isIdle, mutate, mutateAsync } = useMutation({
    mutationKey: ['reviewFormData', { productId, locale }], // TODO: use `useMutationState` from react-query v5 to sync, instead of using a context provider
    mutationFn: () => fetchReviewFormData(productId, locale),
    onSuccess: onSuccess,
    onError: onError,
  });
  return {
    data: data?.data,
    hasSubmitted: data?.hasSubmitted,
    status,
    isPending,
    isInitialLoading: isPending || isIdle,
    mutate,
    mutateAsync,
  };
};
